import React, { useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { saveSession } from '../../shared/auth/portalSession';
import { verifyAdminApi, type AdminUser } from '../api/adminApi';

interface LoginProps {
  onLoginSuccess: (user: AdminUser) => void;
}

const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGoogleSuccess = async (tokenResponse: any) => {
    try {
      setLoading(true);
      setError(null);

      const credential = tokenResponse.credential;
      if (!credential) throw new Error('Google credential missing');
      const response = await verifyAdminApi(credential);

      if (response.exists && response.user) {
        saveSession('admin', { user: response.user, token: response.token!, expiresAt: response.expiresAt! });
        onLoginSuccess(response.user);
      } else {
        // Admin doesn't exist in database
        setError('המשתמש לא רשום במערכת. אנא פנה למנהל המערכת להוספת הרשאות.');
      }
    } catch (err: any) {
      console.error('Admin login error:', err);
      setError(err.message || 'התחברות נכשלה. אנא נסה שוב.');
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-teal-900 flex items-center justify-center px-4 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -inset-10 opacity-30">
          <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-teal-500 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse"></div>
          <div className="absolute top-1/3 right-1/4 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse animation-delay-2000"></div>
          <div className="absolute bottom-1/4 left-1/3 w-72 h-72 bg-cyan-400 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse animation-delay-4000"></div>
        </div>
      </div>

      {/* Grid Pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')] opacity-20"></div>
      
      <div className="relative z-10 max-w-md w-full">
        {/* Logo and Header */}
        <div className="text-center mb-10">
          {/* Medical Logo */}
          <div className="flex justify-center mb-8">
            <div className="relative group">
              <div className="w-32 h-32 bg-gradient-to-br from-white to-slate-100 rounded-3xl flex items-center justify-center shadow-2xl transform hover:scale-105 transition-all duration-300 border border-white/20">
                {/* Medical Cross with Heart */}
                <div className="relative">
                  {/* Heart Shape */}
                  <svg className="w-16 h-16 text-teal-600 absolute inset-0" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                  </svg>
                  {/* Medical Cross */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-8 h-2 bg-white rounded-full shadow-lg"></div>
                    <div className="absolute w-2 h-8 bg-white rounded-full shadow-lg"></div>
                  </div>
                </div>
              </div>
              {/* Glow Effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-blue-500 rounded-3xl blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-300 animate-pulse"></div>
            </div>
          </div>
          
          <div className="backdrop-blur-sm bg-white/10 rounded-2xl p-6 border border-white/20">
            <h1 className="text-4xl font-bold text-white mb-3 tracking-tight">מרכז מונא הרפואי
</h1>
            <p className="text-xl text-teal-200 font-medium mb-2">מערכת ניהול מתקדמת</p>
            <p className="text-slate-300 text-sm">פורטל מנהלים</p>
          </div>
        </div>

        {/* Login Card with Glass Morphism */}
        <div className="backdrop-blur-xl bg-white/10 rounded-3xl shadow-2xl p-8 border border-white/20 relative">
          {/* Subtle Inner Glow */}
          <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent rounded-3xl"></div>
          
          <div className="relative z-10">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-3">
                התחברות למערכת
              </h2>
              <p className="text-slate-200 text-sm leading-relaxed">
                התחבר באמצעות חשבון Google מורשה<br />
                לגישה מאובטחת למערכת הניהול
              </p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-500/20 backdrop-blur border border-red-300/30 text-red-100 rounded-2xl text-center text-sm shadow-lg">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  שגיאה
                </div>
                {error}
              </div>
            )}

            {/* Google Login Button Container */}
            <div className="flex justify-center mb-6">
              <GoogleLogin onSuccess={handleGoogleSuccess}
                onError={() => setError('התחברות באמצעות Google נכשלה. אנא נסה שוב.')}
                locale="he" text="signin_with" />
            </div>

            {loading && (
              <div className="flex items-center justify-center gap-3 mb-6">
                <div className="relative">
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-white/30 border-t-white"></div>
                  <div className="absolute inset-0 animate-ping rounded-full h-6 w-6 border border-white/20"></div>
                </div>
                <span className="text-sm text-white font-medium">מתחבר למערכת...</span>
              </div>
            )}

            {/* Security Info */}
            <div className="pt-6 border-t border-white/20">
              <div className="flex items-center justify-center gap-2 text-xs text-slate-300 mb-3">
                <svg className="w-4 h-4 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>רק משתמשים מורשים יכולים להיכנס למערכת</span>
              </div>
            </div>
          </div>
        </div>

        {/* Security Badge */}
        <div className="mt-8 text-center">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-white/5 backdrop-blur-sm rounded-full shadow-lg border border-white/10 hover:bg-white/10 transition-all duration-300">
            <div className="flex items-center gap-2">
              <div className="relative">
                <svg className="w-5 h-5 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <div className="absolute -inset-1 bg-teal-400/20 rounded-full animate-ping"></div>
              </div>
              <span className="text-xs text-white font-medium">אבטחה מתקדמת</span>
            </div>
            <div className="h-4 w-px bg-white/20"></div>
            <span className="text-xs text-slate-300">Google OAuth 2.0</span>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-slate-400">
            © 2025 מרכז מונא הרפואי
 - כל הזכויות שמורות
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
